import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import DVCViewer from './DVCViewer';
import { FormPicker } from './FormPicker';
import { SpecForm } from './SpecForm';
import DryRunPreview, { type DryRunPreviewData } from './DryRunPreview';
import { Button, Card, EmptyState, Field, SegmentedControl, inputStyle, tokens } from './ui/controls';

type Mode = 'create' | 'view';

type RegistryDataset = { dataset_id: string; name: string; version: string };

type DryRunResult = DryRunPreviewData & {
  synthetic_bboxes?: boolean;
  rows_filled?: number;
  rows_skipped?: number;
};

export default function DatasetRegistryTab({
  onTrain,
  onInfer,
}: {
  onTrain: (name: string, version: string) => void;
  onInfer: (name: string, version: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('create');
  const [spec, setSpec] = useState<Record<string, unknown>>({});
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [registry, setRegistry] = useState<RegistryDataset[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [accepted, setAccepted] = useState<{ name: string; version: string } | null>(null);
  const [sourceDataset, setSourceDataset] = useState('');

  useEffect(() => {
    api.dataviewer.listRegistryDatasets()
      .then((res) => setRegistry(res.data.datasets || []))
      .catch((e) => setMessage(String(e)));
  }, []);

  const selected = registry.find((row) => row.dataset_id === selectedId);

  const runDry = async () => {
    setBusy(true);
    setMessage('');
    try {
      const res = await api.dataviewer.dryRunDataset(spec);
      const data = res.data as DryRunResult;
      setDryRun(data);
      setMessage(data.dataset_id ? `Preview ${data.dataset_id} (${data.num_records ?? 0} rows)` : 'Dry-run ok');
    } catch (e: any) {
      setMessage(e.response?.data?.detail || e.message || 'Dry-run failed');
      setDryRun(null);
    } finally {
      setBusy(false);
    }
  };

  const fillBoxes = async () => {
    const csv_text = dryRun?.csv_text;
    if (!csv_text || !sourceDataset.trim()) return;
    setBusy(true);
    setMessage('');
    try {
      const res = await api.dataviewer.fillRandomBoxes({
        csv_text,
        source_dataset: sourceDataset.trim(),
      });
      const data = res.data as DryRunResult;
      setDryRun({ ...dryRun, ...data, synthetic_bboxes: true });
      const skipped = data.rows_skipped ? `, ${data.rows_skipped} skipped` : '';
      setMessage(`Filled random boxes on ${data.rows_filled ?? 0} rows${skipped}`);
    } catch (e: any) {
      setMessage(e.response?.data?.detail || e.message || 'Fill failed');
    } finally {
      setBusy(false);
    }
  };

  const accept = async () => {
    const csv_text = dryRun?.csv_text;
    if (!csv_text) {
      setMessage('Run dry-run first');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const res = await api.dataviewer.acceptDataset({ spec, csv_text });
      const sql = spec.sql_config && typeof spec.sql_config === 'object'
        ? spec.sql_config as Record<string, unknown>
        : spec;
      const name = String(sql.name || spec.name || '');
      const version = String(sql.version || spec.version || '');
      if (name && version) setAccepted({ name, version });
      const listed = await api.dataviewer.listRegistryDatasets();
      setRegistry(listed.data.datasets || []);
      setMessage(`Accepted: ${JSON.stringify(res.data)}`);
    } catch (e: any) {
      setMessage(e.response?.data?.detail || e.message || 'Accept failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Datasets</h2>
      <Card title="Registry">
        {registry.length === 0 ? (
          <EmptyState>No registry datasets yet. Create one below, then train or infer from it here.</EmptyState>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ display: 'grid', gap: '0.35rem', maxHeight: 240, overflow: 'auto' }}>
              {registry.map((row) => {
                const selectedRow = row.dataset_id === selectedId;
                return (
                  <button
                    key={row.dataset_id}
                    type="button"
                    onClick={() => setSelectedId(row.dataset_id)}
                    style={{
                      textAlign: 'left',
                      padding: '0.45rem 0.65rem',
                      borderRadius: tokens.radius,
                      border: `1px solid ${selectedRow ? tokens.primary : tokens.border}`,
                      background: selectedRow ? tokens.primarySoft : tokens.surface,
                      cursor: 'pointer',
                    }}
                  >
                    {row.name}@{row.version}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Button
                variant="primary"
                disabled={!selected}
                onClick={() => selected && onTrain(selected.name, selected.version)}
              >
                Train
              </Button>
              <Button
                disabled={!selected}
                onClick={() => selected && onInfer(selected.name, selected.version)}
              >
                Infer
              </Button>
            </div>
          </div>
        )}
      </Card>
      <SegmentedControl
        options={[
          { id: 'create', label: 'Create' },
          { id: 'view', label: 'View' },
        ]}
        value={mode}
        onChange={setMode}
      />

      {mode === 'view' ? (
        <div style={{ marginTop: '1rem' }}>
          <DVCViewer />
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
          <Card title="Create">
            <div style={{ display: 'grid', gap: '1rem' }}>
              <FormPicker kind="dataset" onSpec={setSpec} />
              <SpecForm kind="dataset" spec={spec} onChange={setSpec} />
              {dryRun?.synthetic_bboxes && (
                <Field label="Production dataset">
                  <input
                    style={inputStyle}
                    value={sourceDataset}
                    placeholder="name@version"
                    onChange={(e) => setSourceDataset(e.target.value)}
                  />
                </Field>
              )}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <Button onClick={runDry} disabled={busy}>{busy ? 'Working…' : 'Dry-run'}</Button>
                {dryRun?.synthetic_bboxes && (
                  <Button onClick={fillBoxes} disabled={busy || !dryRun.csv_text || !sourceDataset.trim()}>
                    Fill random boxes
                  </Button>
                )}
                <Button variant="primary" onClick={accept} disabled={busy || !dryRun?.csv_text}>
                  Accept to registry
                </Button>
                {accepted && (
                  <Button onClick={() => onInfer(accepted.name, accepted.version)}>
                    Infer
                  </Button>
                )}
                {message && <span style={{ fontSize: '0.85rem', color: tokens.textMuted }}>{message}</span>}
              </div>
            </div>
          </Card>
          <DryRunPreview dryRun={dryRun} />
        </div>
      )}
    </div>
  );
}
